/**
 * V2 Animation Section Field Configuration (schema 2.17.0)
 *
 * Dedicated "Animation" editor section — single home for per-screen motion
 * settings. Controls moved here from Basic Config (Screen Entrance + legacy
 * Animation Enabled) so every StepKind, including Carousel / ProgressSteps /
 * FlexibleLayout, can configure entrance motion.
 *
 * Runtime priority (resolveScreenAnimationPlan):
 *   content.screenAnimation > content.animated:true (default fadeIn)
 *   > FlowDocument.defaultScreenAnimation (theme)
 *
 * Only runtime-implemented values are offered: presets come from
 * v2AnimationPresetOptions (registry-derived), easing list mirrors the theme
 * editor's 7 curves (web transitionSpec/easing map — bounce/elastic curves
 * degrade at runtime and are intentionally NOT offered).
 */

import type { FieldConfig } from '../types/editorFieldTypes';

/**
 * Screen Entrance (nullable object, checkbox-gated)
 *
 * Path: content.screenAnimation
 * Enabled → { enterAnimation: { preset, timing: { duration } }, stagger: { delayPerItem } }
 */
export const V2_SCREEN_ANIMATION_CONFIG: FieldConfig = {
  path: 'content.screenAnimation',
  label: 'Screen Entrance',
  type: 'toggle',
  defaultValue: null,
  disableBehavior: 'null',
  guideKey: 'screenedit-animation-screen-entrance',
};

export const V2_SCREEN_ANIMATION_PRESET_CONFIG: FieldConfig = {
  path: 'content.screenAnimation.enterAnimation.preset',
  label: 'Entrance Preset',
  type: 'select',
  defaultValue: 'fadeInUp',
  guideKey: 'screenedit-animation-screen-entrance-preset',
};

export const V2_SCREEN_ANIMATION_DURATION_CONFIG: FieldConfig = {
  path: 'content.screenAnimation.enterAnimation.timing.duration',
  label: 'Duration (ms)',
  type: 'number',
  defaultValue: 450,
  min: 50,
  max: 10000,
  guideKey: 'screenedit-animation-screen-entrance-duration',
};

export const V2_SCREEN_ANIMATION_STAGGER_CONFIG: FieldConfig = {
  path: 'content.screenAnimation.stagger.delayPerItem',
  label: 'Stagger (ms)',
  type: 'number',
  defaultValue: 80,
  min: 0,
  max: 1000,
  guideKey: 'screenedit-animation-screen-entrance-stagger',
};

/**
 * Legacy Animation Enabled toggle (content.animated).
 *
 * Rendered in the Animation section ONLY when the screen already carries
 * `animated: true` and no `screenAnimation` override — legacy data stays
 * editable, clean screens never see the control. The guideKey keeps its
 * original id so existing guide content and support-flow image bindings
 * remain valid after the move out of Basic Config.
 */
export const V2_ANIMATED_CONFIG: FieldConfig = {
  path: 'content.animated',
  label: 'Animation Enabled',
  type: 'toggle',
  defaultValue: false,
  isNullable: false,
  guideKey: 'screenedit-basic-config-animation-enabled',
};

/**
 * Advanced: entrance easing (7 runtime-supported curves — same set the theme
 * editor offers; see themes V2AnimationsSection THEME_EASING_OPTIONS).
 */
export const V2_ANIMATION_EASING_OPTIONS = [
  { value: 'easeInOutCubic', label: 'Ease In Out Cubic' },
  { value: 'easeOut', label: 'Ease Out' },
  { value: 'easeInOut', label: 'Ease In Out' },
  { value: 'easeOutCubic', label: 'Ease Out Cubic' },
  { value: 'easeOutExpo', label: 'Ease Out Expo' },
  { value: 'easeOutBack', label: 'Ease Out Back' },
  { value: 'linear', label: 'Linear' },
] as const;

export const V2_ANIMATION_EASING_CONFIG: FieldConfig = {
  path: 'content.screenAnimation.enterAnimation.timing.easing',
  label: 'Easing',
  type: 'select',
  options: [...V2_ANIMATION_EASING_OPTIONS],
  defaultValue: 'easeInOutCubic',
  guideKey: 'screenedit-animation-easing',
};

/**
 * Advanced: per-element entrance overrides.
 *
 * Overrides are written into `content.elementAnimations[]` (the resolver's
 * HIGHEST-priority layer) rather than per-entity `*.animation` fields, because
 * elementAnimations refs use the exact editPath convention every mount point
 * looks up (`useElementAnimation` does exact-ref lookup). Per-entity
 * `copy.animation` maps to ref `copy`, which has no mount on HeroContent
 * (titles render via Text at `copy.title` — no CopyBlock wrapper), so it would
 * silently not play there. elementAnimations entries with `copy.title` /
 * `copy.subtitle` work uniformly on every StepKind.
 *
 * One UI row may own several refs (Title & Subtitle writes both copy refs with
 * the same preset). "Inherit" removes the row's refs from the array — the slot
 * falls back to the Screen Entrance distribution.
 *
 * A slot row renders ONLY when its content block is present on the edited
 * step (mirrors the resolver's SCREEN_SLOTS presence checks).
 */
export interface V2AnimationSlotConfig {
  /** Stable key used in testids: `input-animation-slot-{key}` */
  key: string;
  /** Human label in the Advanced list */
  label: string;
  /** Content block(s) whose presence on the step reveals this row */
  presencePaths: string[];
  /** elementAnimations[].elementRef values this row owns (no `content.` prefix) */
  elementRefs: string[];
}

export const V2_ANIMATION_SLOT_CONFIGS: V2AnimationSlotConfig[] = [
  { key: 'header', label: 'Header', presencePaths: ['content.header'], elementRefs: ['header'] },
  { key: 'copy', label: 'Title & Subtitle', presencePaths: ['content.copy.title', 'content.copy.subtitle'], elementRefs: ['copy.title', 'copy.subtitle'] },
  { key: 'hero', label: 'Hero image', presencePaths: ['content.hero'], elementRefs: ['hero'] },
  { key: 'media', label: 'Media', presencePaths: ['content.featuredMedia'], elementRefs: ['featuredMedia'] },
  { key: 'actionBar', label: 'Action Bar', presencePaths: ['content.actionBar'], elementRefs: ['actionBar'] },
  { key: 'products', label: 'Products', presencePaths: ['content.paywall.products'], elementRefs: ['paywall.products'] },
];

/** Path of the elementAnimations override array on the step */
export const V2_ELEMENT_ANIMATIONS_PATH = 'content.elementAnimations';
